<template>
  <div class="modal-overlay">
    <div class="modal-content">
      <div class="modal-header">
        <div class="modal-title-wrap">
          <div class="modal-icon">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M16 21v-2a4 4 0 00-4-4H6a4 4 0 00-4 4v2"/><circle cx="9" cy="7" r="4"/><line x1="19" y1="8" x2="19" y2="14"/><line x1="22" y1="11" x2="16" y2="11"/></svg>
          </div>
          <h2>Ajouter un employé</h2>
        </div>
        <button class="close-btn" @click="$emit('cancel')">✕</button>
      </div>

      <form @submit.prevent="submitForm" enctype="multipart/form-data" class="form">
        <div class="form-section">
          <div class="form-group">
            <label class="form-label">Poste</label>
            <div v-if="lockedPost" class="readonly-field">{{ lockedPost }}</div>
            <select v-else v-model="staff.Post_name" class="form-input" required>
              <option disabled value="">Sélectionner un poste</option>
              <option v-for="post in posts" :key="post.Post_name" :value="post.Post_name">{{ post.Post_name }}</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Groupe horaire</label>
            <select v-model="staff.Group_name" class="form-input" required>
              <option disabled value="">Sélectionner un groupe horaire</option>
              <option v-for="group in workGroups" :key="group.group_name" :value="group.group_name">
                {{ group.group_name }} · {{ group.shift_start }} - {{ group.shift_end }}
              </option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Prénom</label>
            <input v-model="staff.First_name" type="text" class="form-input" autocomplete="given-name" required />
          </div>

          <div class="form-group">
            <label class="form-label">Nom</label>
            <input v-model="staff.Last_name" type="text" class="form-input" autocomplete="family-name" required />
          </div>

          <div class="form-group">
            <label class="form-label">Date de début</label>
            <input v-model="staff.Created_at" type="date" class="form-input" required />
          </div>

          <div class="form-group">
            <label class="form-label">CIN</label>
            <input v-model="staff.CIN" type="text" class="form-input" />
          </div>

          <div class="form-group">
            <label class="form-label">Téléphone</label>
            <input v-model="staff.Phone" type="tel" class="form-input" autocomplete="tel" />
          </div>

          <div class="form-group full">
            <label class="form-label">Adresse</label>
            <input v-model="staff.Adress" type="text" class="form-input" autocomplete="street-address" />
          </div>

          <div class="form-group">
            <label class="form-label">Statut d'entrée</label>
            <select v-model.number="staff.Is_trial" class="form-input">
              <option :value="0">Direct</option>
              <option :value="1">Période d'essai</option>
            </select>
          </div>

          <div class="form-group">
            <label class="form-label">Salaire mensuel (MAD)</label>
            <input v-model="staff.Salary" type="number" min="0" step="1" class="form-input" inputmode="numeric" :disabled="staff.Is_trial === 1" />
            <small v-if="staff.Is_trial === 1" class="field-note">Le salaire sera affecté après confirmation.</small>
          </div>

          <div class="form-group">
            <label class="form-label">Seuil avance semaine (MAD)</label>
            <input v-model="staff.Advance_weekly_limit" type="number" min="0" step="1" class="form-input" inputmode="numeric" />
          </div>

          <div class="form-group">
            <label class="form-label">Photo</label>
            <div class="file-input-wrapper">
              <input type="file" id="pic-create" accept="image/*" @change="handleFileChange" class="file-input" />
              <label for="pic-create" class="file-label">
                <span>{{ selectedFile ? selectedFile.name : 'Choisir une photo' }}</span>
              </label>
            </div>
          </div>
        </div>

        <div v-if="success" class="alert alert-success">✓ Employé ajouté avec succès! <strong>ID: {{ createdStaffId }}</strong></div>
        <div v-if="error" class="alert alert-error">✕ {{ error }}</div>

        <div class="form-actions">
          <button type="button" @click="$emit('cancel')" class="btn btn-secondary">Annuler</button>
          <button type="submit" :disabled="loading" class="btn btn-primary">
            <svg v-if="!loading" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><polyline points="20 6 9 17 4 12"/></svg>
            {{ loading ? 'Ajout...' : "Ajouter l'employé" }}
          </button>
        </div>
      </form>
    </div>
  </div>
</template>

<script>
export default {
  name: 'AppCreateStaff',
  props: {
    lockedPost: {
      type: String,
      default: '',
    },
  },
  emits: ['created', 'cancel'],
  data() {
    return {
      staff: { Post_name: this.lockedPost || '', Group_name: '', First_name: '', Last_name: '', Created_at: this.todayForInput(), CIN: '', Phone: '', Adress: '', Salary: '', Is_trial: 0, Advance_weekly_limit: 0, Salary_effective_from: this.todayForInput() },
      posts: [],
      workGroups: [],
      selectedFile: null,
      loading: false,
      success: false,
      error: null,
      createdStaffId: null,
    };
  },
  mounted() {
    this.fetchPosts();
    this.fetchGroups();
  },
  watch: {
    lockedPost(value) {
      if (value) this.staff.Post_name = value;
    },
  },
  methods: {
    todayForInput() {
      const now = new Date();
      if (now.getHours() < 5) {
        now.setDate(now.getDate() - 1);
      }
      const offsetMs = now.getTimezoneOffset() * 60000;
      return new Date(now.getTime() - offsetMs).toISOString().slice(0, 10);
    },
    async fetchPosts() {
      try {
        const response = await fetch(`/api-pointage/fetchposts`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Erreur ${response.status}`);
        const text = await response.text();
        const data = text ? JSON.parse(text) : [];
        this.posts = Array.isArray(data) ? data : [];
        if (this.lockedPost) {
          this.staff.Post_name = this.lockedPost;
        }
      } catch (err) {
        console.error('Error fetching posts:', err);
        this.posts = [];
      }
    },
    async fetchGroups() {
      try {
        const response = await fetch(`/api-pointage/fetchgroups`, {
          credentials: 'include',
          cache: 'no-store',
        });
        if (!response.ok) throw new Error(`Erreur ${response.status}`);
        const text = await response.text();
        const data = text ? JSON.parse(text) : [];
        this.workGroups = Array.isArray(data) ? data : [];
        if (!this.staff.Group_name && this.workGroups.length) {
          this.staff.Group_name = this.workGroups[0].group_name;
        }
      } catch (err) {
        console.error('Error fetching groups:', err);
        this.workGroups = [];
      }
    },
    handleFileChange(e) { this.selectedFile = e.target.files[0]; },
    async submitForm() {
      this.loading = true; this.error = null;
      try {
        if (this.lockedPost) {
          this.staff.Post_name = this.lockedPost;
        }
        if (!this.staff.Group_name) {
          throw new Error('Groupe horaire requis');
        }
        if (this.staff.Is_trial === 1) {
          this.staff.Salary = 0;
        }
        this.staff.Salary_effective_from = this.staff.Created_at;
        const formData = new FormData();
        Object.entries(this.staff).forEach(([k, v]) => formData.append(k, v));
        if (this.selectedFile) formData.append('pic', this.selectedFile);
        const response = await fetch('/api-pointage/staff', { method: 'POST', body: formData, credentials: 'include' });
        if (!response.ok) {
          const text = await response.text();
          let message = `Erreur ${response.status}`;
          if (text) {
            try {
              const e = JSON.parse(text);
              message = e.error || e.message || message;
            } catch {
              message = text;
            }
          }
          throw new Error(message);
        }
        const result = await response.json();
        this.createdStaffId = result.Id || result.id || 'N/A';
        this.success = true;
        setTimeout(() => this.$emit('created'), 1200);
      } catch (err) { this.error = err.message; }
      finally { this.loading = false; }
    }
  }
};
</script>

<style scoped>
.modal-overlay {
  position: fixed;
  inset: 0;
  z-index: 2000;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 18px;
  background: rgba(15, 23, 42, 0.62);
  backdrop-filter: blur(6px);
}

.modal-content {
  width: min(760px, calc(100vw - 36px));
  max-height: min(760px, calc(100vh - 36px));
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #fff;
  border-radius: 12px;
  box-shadow: 0 24px 70px rgba(15, 23, 42, 0.32);
}

.modal-header {
  position: sticky;
  top: 0;
  z-index: 2;
  min-height: 66px;
  padding: 16px 20px;
  background: #fff;
  border-bottom: 1px solid #dbe3f1;
}

.modal-title-wrap {
  display: flex;
  align-items: center;
  gap: 10px;
}

.modal-title-wrap h2 {
  margin: 0;
  font-size: 18px;
}

.modal-icon {
  width: 34px; height: 34px;
  background: var(--primary-glow);
  border-radius: 8px;
  display: flex; align-items: center; justify-content: center;
  color: var(--primary);
}
.modal-icon svg { width: 16px; height: 16px; }

.close-btn {
  width: 34px;
  height: 34px;
  border: 0;
  border-radius: 8px;
  background: var(--gray-100);
  color: var(--gray-600);
  cursor: pointer;
  font-weight: 800;
}

.close-btn:hover {
  background: var(--danger-bg);
  color: var(--danger);
}

.form {
  padding: 18px 20px 0;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.form-section {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px 16px;
}

.form-section > .form-group:first-child,
.form-section > .form-group.full {
  grid-column: 1 / -1;
}

.form-input {
  min-height: 42px;
}

.field-note {
  display: block;
  margin-top: 7px;
  color: var(--gray-500);
  font-size: 12px;
  font-weight: 700;
}

.readonly-field {
  min-height: 42px;
  display: flex;
  align-items: center;
  padding: 0 14px;
  border: 1px solid #dbe3f1;
  border-radius: 8px;
  background: #f8fafc;
  color: var(--gray-900);
  font-weight: 800;
}

.file-label {
  min-height: 42px;
  border-radius: 8px;
}

.form-actions {
  position: sticky;
  bottom: 0;
  z-index: 2;
  margin: 0 -20px;
  padding: 12px 20px 14px;
  background: #fff;
  border-top: 1px solid #dbe3f1;
  display: flex;
  justify-content: flex-end;
  gap: 10px;
}

.form-actions .btn {
  min-height: 40px;
}

.form-actions .btn svg {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
}

@media (max-width: 680px) {
  .modal-overlay {
    align-items: stretch;
    padding: 0;
  }

  .modal-content {
    width: 100%;
    max-height: 100vh;
    border-radius: 0;
  }

  .modal-header {
    min-height: 58px;
    padding: 12px 14px;
  }

  .form {
    padding: 12px 12px 0;
    gap: 10px;
  }

  .form-section {
    grid-template-columns: 1fr;
    gap: 12px;
  }

  .form-actions {
    margin: 0 -12px;
    padding: 12px 16px 14px;
  }

  .form-actions .btn {
    flex: 1;
    min-height: 42px;
    font-size: 13px;
  }
}
</style>
